import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import {
    Cart,
    CartException,
    PaymentCart,
    PaymentMethods,
    PaymentResponse,
    PaymentToken,
    ProcessorName,
    SaleStatus,
} from '@cargos/sprintpay-models';
import { AdvancedFlowApiService, AdyenAdvancedComponent, AdyenComponentResponse } from '@cargos/sprintpay-ui';
import { combineLatest, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import {
    CartBillService,
    PaymentCartService,
    PaymentConfirmationService,
    PaymentMethodsService,
    SessionService,
} from 'src/app/shared/services';
import Swal from 'sweetalert2';
import { PaymentMethodGuest } from '../../shared/models/payment-method.model';
import { PaymentMethodsHandlerService } from '../../shared/services/payment-methods-handler.service';

import { PaymentMethodSelected } from '../shared/models';

@Component({
    standalone: true,
    imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule, AdyenAdvancedComponent],
    selector: 'app-lib-paypal-button',
    templateUrl: './paypal-button.component.html',
    styleUrl: './paypal-button.component.scss',
})
export class PaypalButtonComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();
    public isPaying = true;
    public hideButton = false;
    public isLoading = false;
    public processorName: ProcessorName = ProcessorName.Adyen;
    public paymentCart: PaymentCart[] = [];
    public totalAmount: number = 0;
    public isGuest: boolean = false;

    private paymentToken: PaymentToken | null;
    private cartBill: Cart;

    @Input() cartBillTotal: number;
    @Output() eventSubmitPayment = new EventEmitter<string>();

    private paymentMethodsService = inject(PaymentMethodsService);
    private advancedFlowApiService = inject(AdvancedFlowApiService);
    private paymentConfirmationService = inject(PaymentConfirmationService);

    constructor(
        private paymentCartService: PaymentCartService,
        private cartBillService: CartBillService,
        private sessionService: SessionService,
        private router: Router,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {}

    ngOnInit(): void {
        this.subscribeCart();
        this.subscribePaymentMethods();
        this.subscribeToCart();
        this.getTotalAmount();
        this.enablePayPalButtonAdyenFlow();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribeCart(): void {
        combineLatest([this.getCart(), this.getPaymentToken()])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: ([cart, paymentMethodToken]) => {
                    this.cartBill = cart;
                    this.paymentToken = paymentMethodToken;
                },
            });
    }

    getCart(): Observable<Cart> {
        return this.cartBillService.getCartBill$();
    }

    getPaymentToken(): Observable<PaymentToken | null> {
        return this.sessionService.isAuthenticated$().pipe(
            switchMap((isAuthenticated) => {
                this.isGuest = !isAuthenticated;
                if (isAuthenticated) {
                    return this.paymentMethodsHandlerService.getPaymentMethodSelected$().pipe(
                        switchMap((method: PaymentMethodSelected | null) => {
                            if (method) {
                                return this.paymentMethodsService.getPaymentToken(method.method, method?.token);
                            }
                            return of(null);
                        })
                    );
                }
                return this.paymentMethodsHandlerService.getPaymentMethodGuest().pipe(
                    switchMap((method: PaymentMethodGuest | null) => {
                        if (method?.paymentMethod) {
                            return this.paymentMethodsService.getPaymentToken(method.paymentMethod);
                        }
                        return of(null);
                    })
                );
            })
        );
    }

    onPaymentComplete(cartPayResponses: PaymentResponse[]): void {
        const successfulPayments = cartPayResponses.filter(
            (payResult) => payResult.saleStatus === SaleStatus.SALE_SUCCESS
        );

        if (successfulPayments.length) {
            this.router.navigate(['/cart/confirmation']);
        }
    }

    adyenPaypalResponse(adyenResponse: AdyenComponentResponse | null): void {
        if (!adyenResponse) {
            return;
        }
        if (adyenResponse.type === 'completed') {
            const responses: PaymentResponse[] = adyenResponse.paymentResponse ? [adyenResponse.paymentResponse] : [];
            const paymentCartService = this.paymentCartService.getCartResponses(responses);
            this.paymentConfirmationService.setConfirmationInformation(responses);

            const isCartAboveThreshold = this.cartBillService.isCartAboveThreshold();
            this.paymentCartService
                .handlePaymentResults(responses, isCartAboveThreshold)
                .pipe(take(1))
                .subscribe({
                    next: () => {
                        this.onPaymentComplete(paymentCartService);
                    },
                    error: (cartException: CartException) => {
                        Swal.fire({
                            icon: 'warning',
                            title: cartException.title,
                            text: cartException.description,
                        });
                    },
                });
        }
    }

    private subscribePaymentMethods(): void {
        this.paymentMethodsService
            .getPaymentMethods$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (paymentMethods) => {
                    const paymentMethod = paymentMethods.find(
                        (paymentMethod) => paymentMethod.name === PaymentMethods.PAYPAL
                    );
                    if (paymentMethod && paymentMethod.processorName) {
                        this.processorName = paymentMethod.processorName;
                    }
                    this.advancedFlowApiService.setPaymentMethod('PayPal');
                    this.enabledAdyenPaypal();
                },
            });
    }

    private subscribeToCart(): void {
        this.cartBillService
            .getCartPaymentRequests$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((paymentCart) => {
                this.paymentCart = paymentCart;
            });
    }

    private getTotalAmount(): void {
        this.cartBillService
            .getTotalCart$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((total) => {
                this.totalAmount = total;
            });
    }

    private enabledAdyenPaypal(): void {
        this.paymentCartService
            .isAllowedToMakeAPayment$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (status: boolean) => {
                    this.advancedFlowApiService.setStatusPayButton(status);
                },
            });
    }

    private enablePayPalButtonAdyenFlow(): void {
        this.advancedFlowApiService
            .getIsAdyenLoaded$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((status: boolean) => {
                this.isPaying = !status;
            });
    }
}
